import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik, getIn, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CheckboxFormik from '~/components/CheckboxFormik';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  concreteSelectedLobs,
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import InfoIcon from '~/components/icons/InfoIcon';
import COMMUNICATION_TEMPLATE_ACTION from '~/server_shared/generated-types/COMMUNICATION_TEMPLATE_ACTION';
import { displayCommunicationCategories, getAxiosParamsSerializer, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';
import { sortKeysByField } from '~/Utils/sortKeysByField';

import CardDialog from '../../../CardDialog';
import useOrganization from '../../../OrganizationContext';
import TextFieldFormik, { MultiSelectTextFieldFormik } from '../../../TextFieldFormik';
import { useSysconfig } from '../../SystemConfigurationScreen';

import useFetchAutoCommRecipientOptions from './hooks/useFetchAutoCommRecipientOptions';
import CommunicationConditionsQueryBuilderFormik from './QueryBuilder/CommunicationConditionsQueryBuilderFormik';
import { recipientBaseKey, recipientByScope, senderBaseKey, senderByScope } from './types';

const FIELD_IDS = {
  ID: 'id',
  DISPLAY_NAME: 'display_name',
  SUB_ORGANIZATION_IDS: 'sub_organization_ids',
  LOBS: 'lobs',
  ALL_LOBS: 'is_all_lobs',
  COMMUNICATION_TYPE: 'channel',
  COMMUNICATION_RECIPIENT: 'recipient',
  CC_RECIPIENTS: 'cc_recipients',
  COMMUNICATION_SENDER: 'sender_type',
  COMMUNICATION_TEMPLATE: 'generic_template_id',
  EVENT_KEY: 'event_key',
  IS_SUGGESTION: 'is_suggestion',
  CATEGORY: 'category_id',
  // Opaque RQB JSON tree — the only conditions representation for communication
  // rules. The QB wrapper writes the tree here on every change.
  CONDITIONS_JSON: 'conditions_json',
};

// The templates route reads repeated keys (`lobs=a&lobs=b`); axios' default `lobs[]=` form is dropped as unknown.
const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');

const TEMPLATE_NOT_IN_SCOPE_MESSAGE = 'The previously selected template is not available for this scope';

const getRuleValidationSchema = ({ subOrganizationEnabled, enabledCategoryIds, isTemplateNotInScope }) =>
  Yup.object().shape({
    [FIELD_IDS.DISPLAY_NAME]: Yup.string().required('Required'),
    [FIELD_IDS.EVENT_KEY]: Yup.string().required('Required'),
    // A cleared out-of-scope template keeps saying why it was cleared: the field shows its validation
    // error over its helper text once touched or after Save, so the reason must be the error itself.
    [FIELD_IDS.COMMUNICATION_TEMPLATE]: Yup.string().required(
      isTemplateNotInScope ? TEMPLATE_NOT_IN_SCOPE_MESSAGE : 'Required'
    ),
    [FIELD_IDS.COMMUNICATION_RECIPIENT]: Yup.string().required('Required'),
    [FIELD_IDS.COMMUNICATION_SENDER]: Yup.string().required('Required'),
    [FIELD_IDS.COMMUNICATION_TYPE]: Yup.string().required('Required'),
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().of(Yup.number()).min(1, 'Required').required('Required')
      : Yup.array().of(Yup.number()).strip(),
    [FIELD_IDS.LOBS]: Yup.array().of(Yup.string()).min(1, 'Required').required('Required'),
    [FIELD_IDS.IS_SUGGESTION]: Yup.bool(),
    [FIELD_IDS.CATEGORY]: Yup.mixed()
      .nullable()
      .test('category-not-disabled', 'Selected category is disabled and cannot be used', (value) => {
        if (!value) {
          return true;
        }
        return enabledCategoryIds.includes(value);
      }),
  });

const AddOrEditRuleDialog = ({ onSubmit, supportedEvents, onCancel, rule }) => {
  const { subOrganizationEnabled, communicationCategories } = useOrganization();
  const [isTemplateNotInScope, setIsTemplateNotInScope] = useState(false);

  const enabledCategoryIds = communicationCategories
    ?.filter((category) => !category.is_disabled)
    .map((category) => category.id);

  return (
    <Formik
      initialValues={{
        [FIELD_IDS.ID]: rule?.id || '',
        [FIELD_IDS.DISPLAY_NAME]: rule?.display_name ?? '',
        [FIELD_IDS.SUB_ORGANIZATION_IDS]: rule
          ? rule.is_all_sub_orgs
            ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
            : rule.sub_organization_ids
          : [],
        // An all-LOBs rule is seeded in the widget's own representation (the "All"
        // sentinel), exactly as `sub_organization_ids` is above. Seeding the server
        // shape (`lobs: [], is_all_lobs: true`) makes the widget's mount conversion
        // rewrite both fields, which the conditions-clearing listener below reads as
        // a user edit — silently discarding the stored conditions tree on every open.
        [FIELD_IDS.LOBS]: rule?.is_all_lobs ? [LobMultiSelectWithChipsFormik.ALL_OPTION.id] : (rule?.lobs ?? []),
        [FIELD_IDS.ALL_LOBS]: rule?.is_all_lobs ?? false,
        [FIELD_IDS.EVENT_KEY]: rule?.event_key ?? '',
        [FIELD_IDS.COMMUNICATION_TEMPLATE]: rule?.generic_template_id ?? '',
        [FIELD_IDS.COMMUNICATION_RECIPIENT]: rule?.recipient ?? '',
        [FIELD_IDS.CC_RECIPIENTS]: rule?.cc_recipients ?? [],
        [FIELD_IDS.COMMUNICATION_SENDER]: rule?.sender_type ?? '',
        [FIELD_IDS.COMMUNICATION_TYPE]: rule?.channel ?? '',
        [FIELD_IDS.IS_SUGGESTION]: !!rule?.is_suggestion,
        [FIELD_IDS.CATEGORY]: rule?.category_id ?? '',
        [FIELD_IDS.CONDITIONS_JSON]: rule?.conditions_json ?? null,
      }}
      validationSchema={getRuleValidationSchema({ subOrganizationEnabled, enabledCategoryIds, isTemplateNotInScope })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          const isAllSubOrganizations = isAllSubOrganizationsSelected(values[FIELD_IDS.SUB_ORGANIZATION_IDS]);
          const submitValues = {
            ...values,
            is_all_sub_orgs: isAllSubOrganizations,
            [FIELD_IDS.SUB_ORGANIZATION_IDS]: isAllSubOrganizations ? [] : values[FIELD_IDS.SUB_ORGANIZATION_IDS],
            [FIELD_IDS.LOBS]: values[FIELD_IDS.ALL_LOBS] ? [] : concreteSelectedLobs(values[FIELD_IDS.LOBS] ?? []),
            context: supportedEvents[values[FIELD_IDS.EVENT_KEY]].scope,
          };
          await onSubmit(submitValues);
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {() => {
        return (
          <AddRuleInner
            onCancel={onCancel}
            supportedEvents={supportedEvents}
            isTemplateNotInScope={isTemplateNotInScope}
            setIsTemplateNotInScope={setIsTemplateNotInScope}
          />
        );
      }}
    </Formik>
  );
};

AddOrEditRuleDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  supportedEvents: PropTypes.object,
  rule: PropTypes.object,
};

const AddRuleInner = ({ supportedEvents, onCancel, isTemplateNotInScope, setIsTemplateNotInScope }) => {
  const {
    subOrganizationEnabled,
    subOrganizations,
    isTwilioEnabled,
    communicationCategories,
    isLoadingCommunicationCategories,
  } = useOrganization();
  const { organization: sysconfigOrg } = useSysconfig();
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext();
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);
  const { isFetchingTemplates, templates, hasLoadedTemplates } = useTemplates({ supportedEvents });
  const selectedTemplateId = values[FIELD_IDS.COMMUNICATION_TEMPLATE];
  // The Template select renders only the scoped list, so a selected template outside it would show
  // blank while Save still sent it. Clear it once that list has loaded, so `required` asks for a
  // pick, and keep the reason on the field until one is made.
  useEffect(() => {
    if (!selectedTemplateId) {
      return;
    }
    if (hasLoadedTemplates && !templates.some(({ id }) => id === selectedTemplateId)) {
      setIsTemplateNotInScope(true);
      // Not validated here: this render's schema still has the plain 'Required' message, and storing
      // it would replace the reason. The next validation runs with the schema that carries it.
      setFieldValue(FIELD_IDS.COMMUNICATION_TEMPLATE, '', false);
    } else {
      setIsTemplateNotInScope(false);
    }
  }, [hasLoadedTemplates, templates, selectedTemplateId, setFieldValue, setIsTemplateNotInScope]);
  const {
    recipientOptions,
    isLoading: isLoadingRecipientOptions,
    isError: isRecipientOptionsError,
  } = useFetchAutoCommRecipientOptions(sysconfigOrg.id);
  // On catalog fetch failure fall back to the static scope allowlist so the
  // picker stays usable. Also always include the currently-selected value so
  // editing a rule whose recipient was later dropped from the catalog (e.g.
  // broker `ContactRole` disabled after the rule was saved) doesn't render
  // a blank Recipient field.
  const selectedRecipient = values[FIELD_IDS.COMMUNICATION_RECIPIENT];
  const allowedRecipientKeys = isRecipientOptionsError
    ? null
    : new Set([...recipientOptions.map((option) => option.key), ...(selectedRecipient ? [selectedRecipient] : [])]);

  const enabledCategories = communicationCategories?.filter((category) => !category.is_disabled) ?? [];

  const displayCategories = displayCommunicationCategories(communicationCategories);

  const { classes } = useStyles();

  const isDisabled = isSubmitting || isLoadingConditions;
  const isTemplateValuesFilled = !isEmpty(values[FIELD_IDS.LOBS]) && !isEmpty(values[FIELD_IDS.COMMUNICATION_TYPE]);

  const subOrganizationsValue = useMemo(() => values[FIELD_IDS.SUB_ORGANIZATION_IDS], [values]);

  const isEditing = !!getIn(values, FIELD_IDS.ID);
  const scope = supportedEvents[values[FIELD_IDS.EVENT_KEY]]?.scope;

  // The To picker's own option list, minus whatever is already the To address.
  const selectedCcRecipients = values[FIELD_IDS.CC_RECIPIENTS] ?? [];
  const offerableCcRecipients = scope
    ? recipientByScope[scope].filter(
        (recipientKey) =>
          recipientKey !== selectedRecipient && (!allowedRecipientKeys || allowedRecipientKeys.has(recipientKey))
      )
    : [];
  // Plus any key this rule already CCs, appended rather than rescued inside the filter above:
  // the filter only walks the scope's own list, so a persisted key from outside it — saved
  // through the API or a CR import — never reaches the re-admission and leaves the admin with a
  // value they cannot clear and a save path that rejects it. Mirrors why the To picker re-adds
  // its selected value, and covers the same case for a key the catalog later dropped (a rule
  // saved with BROKER whose broker `ContactRole` was since disabled). Still never the To
  // address: the promote-to-To listener below drops that one instead of offering it back.
  const ccRecipientOptions = [
    ...offerableCcRecipients,
    ...selectedCcRecipients.filter(
      (recipientKey) => recipientKey !== selectedRecipient && !offerableCcRecipients.includes(recipientKey)
    ),
  ];

  // reset all lobs when sub org changes
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => {
      setFieldValue(FIELD_IDS.LOBS, []);
      setFieldValue(FIELD_IDS.ALL_LOBS, false);
    },
    runOnFirstRender: false,
  });

  // reset channel when lobs updated
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.COMMUNICATION_TYPE],
    onChange: () => {
      if (!values[FIELD_IDS.ALL_LOBS] && !isAllLobsSelected(values[FIELD_IDS.LOBS])) {
        setFieldValue(FIELD_IDS.COMMUNICATION_TEMPLATE, '');
      }
    },
    runOnFirstRender: false,
  });

  // reset channel when channel updated
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.COMMUNICATION_TYPE],
    onChange: () => {
      setFieldValue(FIELD_IDS.COMMUNICATION_TEMPLATE, '');
      // CC is email-only and the API rejects a non-empty list on SMS, so drop it with the
      // field rather than letting a value the user can no longer see 400 the save.
      if (values[FIELD_IDS.COMMUNICATION_TYPE] !== 'EMAIL') {
        setFieldValue(FIELD_IDS.CC_RECIPIENTS, []);
      }
    },
    runOnFirstRender: false,
  });

  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.EVENT_KEY],
    onChange: () => {
      setFieldValue(FIELD_IDS.COMMUNICATION_SENDER, '');
      setFieldValue(FIELD_IDS.COMMUNICATION_RECIPIENT, '');
      // The event carries the scope, and scope decides which recipients are meaningful.
      setFieldValue(FIELD_IDS.CC_RECIPIENTS, []);
    },
    runOnFirstRender: false,
  });

  // CC is not offered on a suggestion rule: the async task returns from
  // `_handle_suggestion_mode_early_exit` before any CC gate runs and the suggestion notification
  // carries no CC of its own, so the API rejects a non-empty list. Drop the value with the field
  // rather than letting one the user can no longer see 400 the save.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.IS_SUGGESTION],
    onChange: () => {
      if (values[FIELD_IDS.IS_SUGGESTION]) {
        setFieldValue(FIELD_IDS.CC_RECIPIENTS, []);
      }
    },
    runOnFirstRender: false,
  });

  // The CC picker hides whatever is currently the To address, so a key promoted to To would
  // otherwise stay in the CC list with no control left to remove it - and the API rejects a
  // rule whose To recipient is also a CC recipient.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.COMMUNICATION_RECIPIENT],
    onChange: () => {
      const recipient = values[FIELD_IDS.COMMUNICATION_RECIPIENT];
      const ccRecipients = values[FIELD_IDS.CC_RECIPIENTS] ?? [];
      if (recipient && ccRecipients.includes(recipient)) {
        setFieldValue(
          FIELD_IDS.CC_RECIPIENTS,
          ccRecipients.filter((recipientKey) => recipientKey !== recipient)
        );
      }
    },
    runOnFirstRender: false,
  });

  // Clear the persisted conditions_json tree whenever a scope dimension
  // changes. The catalog refetches on these keys (event scope filters fields;
  // LOB/sub-org narrow which providers contribute), so a tree authored under
  // the old scope would silently re-validate under the new one if its paths
  // happened to remain valid — saving the rule under a scope the user didn't
  // intend. Matches the banner copy above.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.EVENT_KEY, FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => {
      setFieldValue(FIELD_IDS.CONDITIONS_JSON, null);
    },
    runOnFirstRender: false,
  });

  const subOrganizationIds = subOrganizationEnabled
    ? !isAllSubOrganizationsSelected(subOrganizationsValue)
      ? subOrganizationsValue
      : subOrganizations.map(({ id }) => id)
    : null;

  const lobs = getIn(values, FIELD_IDS.LOBS);
  // "All (inc. future lobs)" is stored as the sentinel id 0, not as a LOB key, so it must not reach
  // the catalog as a LOB named "0". Read off the forwarded value rather than `is_all_lobs`: that flag
  // is set by a change listener one render later, and the intervening render would fire the fetch.
  // The same derivation feeds the `isAllLobs` flag the catalog request carries.
  const isAllLobs = isAllLobsSelected(lobs ?? []);
  const catalogLobs = isAllLobs ? [] : concreteSelectedLobs(lobs ?? []);
  const eventKey = getIn(values, FIELD_IDS.EVENT_KEY);
  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      title={`${isEditing ? 'Edit' : 'Add'} Communication`}
    >
      <AlertBanner
        className="mb-4"
        alertType="info"
        note="Editing the event, sub organizations, or line of businesses will clear all conditions."
        title="Please Note"
        withIcon
      />
      <TextFieldFormik
        id={FIELD_IDS.DISPLAY_NAME}
        label="Rule Name"
        className={cn(classes.textField, 'mb-20')}
        fullWidth
        disabled={isDisabled}
      />
      <div className="flex flex-row items-stretch px-4 py-20">
        <CheckboxFormik id={FIELD_IDS.IS_SUGGESTION} label="Is Suggestion" />
        <Tooltip title="Suggestion will create a notification for the sender with the templated communication">
          <span className="flex w-16 items-center justify-center">
            <InfoIcon size={16} iconColor="currentColor" />
          </span>
        </Tooltip>
      </div>
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Context
        </Heading>
        {subOrganizationEnabled ? (
          <div className="mb-12">
            <SubOrgMultiSelectWithChipsFormik
              id={FIELD_IDS.SUB_ORGANIZATION_IDS}
              label="Sub Organizations"
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        <div className="mb-4">
          <LobMultiSelectWithChipsFormik
            lobsFieldId={FIELD_IDS.LOBS}
            allLobsFieldId={FIELD_IDS.ALL_LOBS}
            {...(subOrganizationEnabled
              ? {
                  subOrganizationIds,
                  subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                  shouldResetOnSubOrgChange: true,
                  disabled: isEmpty(subOrganizationsValue),
                }
              : {})}
          />
        </div>
      </div>
      {displayCategories ? (
        <TextFieldFormik
          id={FIELD_IDS.CATEGORY}
          label="Category"
          className={cn(classes.textField, 'mb-20 w-1/2')}
          fullWidth
          disabled={isSubmitting || isLoadingCommunicationCategories}
          select
          clearable
        >
          {enabledCategories.map(({ id, label }) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextFieldFormik>
      ) : null}
      <TextFieldFormik
        id={FIELD_IDS.EVENT_KEY}
        label="Event"
        className={cn(classes.textField, 'mb-20 w-1/2')}
        fullWidth
        disabled={isSubmitting}
        select
      >
        {sortKeysByField(supportedEvents, 'desc').map((key) => (
          <MenuItem key={key} value={key}>
            {supportedEvents[key].desc}
          </MenuItem>
        ))}
      </TextFieldFormik>
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3}>Details</Heading>
        <div className="grid grid-cols-2 gap-x-16">
          <TextFieldFormik
            id={FIELD_IDS.COMMUNICATION_TYPE}
            label="Communication Type"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
            select
          >
            {Object.keys(COMMUNICATION_TEMPLATE_ACTION.CHANNEL)
              .filter((channelKey) => {
                // Only show SMS if Twilio is enabled
                if (channelKey === 'SMS') {
                  return isTwilioEnabled;
                }
                return true;
              })
              .map((channelKey) => (
                <MenuItem key={channelKey} value={channelKey}>
                  {COMMUNICATION_TEMPLATE_ACTION.CHANNEL[channelKey]}
                </MenuItem>
              ))}
          </TextFieldFormik>
          <TextFieldFormik
            id={FIELD_IDS.COMMUNICATION_RECIPIENT}
            label="Recipient"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting || !scope || isLoadingRecipientOptions}
            select
          >
            {scope
              ? recipientByScope[scope]
                  .filter((recipientKey) => !allowedRecipientKeys || allowedRecipientKeys.has(recipientKey))
                  .map((recipientKey) => (
                    <MenuItem key={recipientKey} value={recipientKey}>
                      {recipientBaseKey[recipientKey]}
                    </MenuItem>
                  ))
              : null}
          </TextFieldFormik>
          <TextFieldFormik
            id={FIELD_IDS.COMMUNICATION_TEMPLATE}
            label="Template"
            className={classes.textField}
            fullWidth
            disabled={isFetchingTemplates || !isTemplateValuesFilled}
            helperText={isTemplateNotInScope ? TEMPLATE_NOT_IN_SCOPE_MESSAGE : ''}
            // After an out-of-scope clear the admin must choose; auto-filling the only option would
            // swap the rule's template without asking.
            autoSelectSingleOption={!isTemplateNotInScope}
            select
          >
            {templates.map(({ id, template_name }) => (
              <MenuItem key={id} value={id}>
                {template_name}
              </MenuItem>
            ))}
          </TextFieldFormik>
          <TextFieldFormik
            id={FIELD_IDS.COMMUNICATION_SENDER}
            label="Sender"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting || !scope}
            select
          >
            {scope
              ? senderByScope[scope].map((senderKey) => (
                  <MenuItem key={senderKey} value={senderKey}>
                    {senderBaseKey[senderKey]}
                  </MenuItem>
                ))
              : null}
          </TextFieldFormik>
          {values[FIELD_IDS.COMMUNICATION_TYPE] === 'EMAIL' && !values[FIELD_IDS.IS_SUGGESTION] ? (
            <MultiSelectTextFieldFormik
              id={FIELD_IDS.CC_RECIPIENTS}
              label="CC Recipients"
              className={classes.textField}
              fullWidth
              disabled={isSubmitting || !scope || isLoadingRecipientOptions}
              options={ccRecipientOptions}
              renderValue={(selected) => selected.map((recipientKey) => recipientBaseKey[recipientKey]).join(', ')}
              renderOption={(recipientKey) => recipientBaseKey[recipientKey]}
              // CC is optional, and a claim-scope rule whose To is already picked routinely
              // leaves a single remaining option - the default single-option auto-fill would
              // copy someone the admin never chose.
              autoSelectSingleOption={false}
            />
          ) : null}
        </div>
      </div>
      <div className="px-4 py-20">
        <CommunicationConditionsQueryBuilderFormik
          fieldId={FIELD_IDS.CONDITIONS_JSON}
          eventKey={eventKey || ''}
          // Every picked LOB: the catalog is the cross-LOB intersection, which
          // is also what save-time validation checks the rule against.
          lobs={catalogLobs}
          isAllLobs={isAllLobs}
          isAllSubOrgs={subOrganizationEnabled ? isAllSubOrganizationsSelected(subOrganizationsValue) : true}
          subOrgIds={subOrganizationEnabled && subOrganizationIds ? subOrganizationIds : []}
          // Disables Save while the catalog is in flight
          // (isDisabled = isSubmitting || isLoadingConditions).
          onLoadingChange={setIsLoadingConditions}
        />
      </div>

      <ButtonsContainer>
        <CancelButton disabled={isDisabled} onClick={onCancel} />
        <Button variant="contained" color="primary" disabled={isDisabled} onClick={handleSubmit}>
          Save
        </Button>
      </ButtonsContainer>
    </CardDialog>
  );
};

AddRuleInner.propTypes = {
  onCancel: PropTypes.func.isRequired,
  supportedEvents: PropTypes.object,
  isTemplateNotInScope: PropTypes.bool.isRequired,
  setIsTemplateNotInScope: PropTypes.func.isRequired,
};

const useTemplates = ({ supportedEvents }) => {
  const { values } = useFormikContext();
  const { organizationId, subOrganizationEnabled } = useOrganization();
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  // True only while `templates` is the successfully loaded list for the current scope; a failed or
  // superseded request never sets it.
  const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);

  const subOrganizationsValue = useMemo(
    () => (subOrganizationEnabled ? values[FIELD_IDS.SUB_ORGANIZATION_IDS] : []),
    [values, subOrganizationEnabled]
  );
  const lobsValue = useMemo(() => values[FIELD_IDS.LOBS], [values]);
  const channelValue = useMemo(() => values[FIELD_IDS.COMMUNICATION_TYPE], [values]);
  const isAllSubOrganizations = useMemo(
    () => subOrganizationEnabled && isAllSubOrganizationsSelected(subOrganizationsValue),
    [subOrganizationEnabled, subOrganizationsValue]
  );
  // Read off the field rather than `is_all_lobs`, which a change listener sets one render later.
  const isAllLobs = isAllLobsSelected(lobsValue ?? []);
  const eventKey = values[FIELD_IDS.EVENT_KEY];

  useEffect(() => {
    // A scope change can resolve the older request last; its list belongs to a scope no longer selected.
    let cancelled = false;
    const getTemplates = async () => {
      try {
        setIsFetchingTemplates(true);
        setHasLoadedTemplates(false);
        const { data } = await axios.get(`/api/v1/generic_templates/organization/${organizationId}`, {
          params: {
            template_type: channelValue.toLowerCase(),
            // "All" on a dimension is sent as its flag, which narrows the list to templates that are
            // All on it; the sentinel is never sent as an id.
            is_all_suborgs: isAllSubOrganizations,
            sub_organization_ids: isAllSubOrganizations ? [] : subOrganizationsValue,
            // Strip the all-LOBs sentinel before it leaves the form, exactly as the submit path
            // does — `concreteSelectedLobs` keeps only string LOB ids, so the widget's numeric `0`
            // "All" sentinel drops to `[]` instead of being sent as a LOB.
            lobs: concreteSelectedLobs(lobsValue ?? []),
            is_all_lobs: isAllLobs,
            // The template must target every selected sub-org and LOB, as the save check requires.
            match_full_scope: true,
            is_enabled: true,
            is_claim_level: supportedEvents[eventKey]?.scope !== 'exposure',
          },
          paramsSerializer: FETCH_PARAMS_SERIALIZER,
        });
        if (cancelled) {
          return;
        }
        setTemplates(data.templates_metadata);
        setHasLoadedTemplates(true);
      } catch (error) {
        await reportAxiosError(error);
      }
      if (!cancelled) {
        setIsFetchingTemplates(false);
      }
    };

    if (channelValue && (!subOrganizationEnabled || !isEmpty(subOrganizationsValue)) && !isEmpty(lobsValue)) {
      getTemplates();
    } else {
      setTemplates([]);
      setHasLoadedTemplates(false);
      // A cancelled request no longer clears the flag, so a scope that stops fetching must.
      setIsFetchingTemplates(false);
    }
    return () => {
      cancelled = true;
    };
  }, [
    organizationId,
    subOrganizationEnabled,
    isAllSubOrganizations,
    subOrganizationsValue,
    channelValue,
    lobsValue,
    isAllLobs,
    supportedEvents,
    eventKey,
  ]);

  return {
    isFetchingTemplates,
    templates,
    hasLoadedTemplates,
  };
};

export default AddOrEditRuleDialog;
// AddRuleInner is exported for testing the Formik change-listener wiring
// (e.g. scope-change reset of conditions_json), and getRuleValidationSchema so those
// tests can validate with the dialog's own schema. Production code should use the
// default export.
export { AddRuleInner, FIELD_IDS, getRuleValidationSchema };
